import type { ReactNode } from 'react';

import type {
  GalleryLayout,
  GalleryPhotoShape,
  GallerySpacing,
  GalleryStyle,
} from 'gallery';

// Small drawings of each look choice for the Design view, in `currentColor` so they follow the
// selected / disabled state of the button they sit in.
function Frame({ children }: { children: ReactNode }) {
  return (
    <svg width="56" height="40" viewBox="0 0 56 40" fill="currentColor" aria-hidden="true">
      <rect x="0.5" y="0.5" width="55" height="39" rx="4" fill="none" stroke="currentColor" opacity="0.35" />
      {children}
    </svg>
  );
}

const SOFT = 0.35;

export function StylePictogram({ style }: { style: GalleryStyle }) {
  if (style === 'COVER') {
    return (
      <Frame>
        <rect x="4" y="4" width="48" height="18" rx="2" />
        <rect x="4" y="25" width="15" height="11" rx="1.5" opacity={SOFT} />
        <rect x="20.5" y="25" width="15" height="11" rx="1.5" opacity={SOFT} />
        <rect x="37" y="25" width="15" height="11" rx="1.5" opacity={SOFT} />
      </Frame>
    );
  }
  if (style === 'EDITORIAL') {
    return (
      <Frame>
        <rect x="4" y="6" width="20" height="3" rx="1.5" />
        <rect x="4" y="12" width="14" height="2" rx="1" opacity={SOFT} />
        <rect x="28" y="4" width="24" height="16" rx="2" opacity={SOFT} />
        <rect x="4" y="20" width="22" height="16" rx="2" opacity={SOFT} />
        <rect x="30" y="24" width="22" height="12" rx="2" opacity={SOFT} />
      </Frame>
    );
  }
  return (
    <Frame>
      <rect x="4" y="4" width="18" height="3" rx="1.5" />
      {[0, 1, 2, 3].map((column) =>
        [0, 1].map((row) => (
          <rect
            key={`${column}-${row}`}
            x={4 + column * 12.25}
            y={11 + row * 13}
            width="11"
            height="11.5"
            rx="1"
            opacity={SOFT}
          />
        ))
      )}
    </Frame>
  );
}

export function LayoutPictogram({ layout }: { layout: GalleryLayout }) {
  if (layout === 'GRID') {
    return (
      <Frame>
        {[0, 1, 2].map((column) =>
          [0, 1].map((row) => (
            <rect
              key={`${column}-${row}`}
              x={5 + column * 15.5}
              y={5 + row * 15.5}
              width="14"
              height="14"
              rx="1.5"
              opacity={SOFT}
            />
          ))
        )}
      </Frame>
    );
  }
  if (layout === 'MASONRY') {
    return (
      <Frame>
        <rect x="5" y="5" width="14" height="18" rx="1.5" opacity={SOFT} />
        <rect x="5" y="25" width="14" height="10" rx="1.5" opacity={SOFT} />
        <rect x="21" y="5" width="14" height="10" rx="1.5" opacity={SOFT} />
        <rect x="21" y="17" width="14" height="18" rx="1.5" opacity={SOFT} />
        <rect x="37" y="5" width="14" height="14" rx="1.5" opacity={SOFT} />
        <rect x="37" y="21" width="14" height="14" rx="1.5" opacity={SOFT} />
      </Frame>
    );
  }
  return (
    <Frame>
      <rect x="11" y="5" width="34" height="24" rx="2" opacity={SOFT} />
      <path d="M7 17 l-3 0 m0 0 l2 -2 m-2 2 l2 2" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path d="M49 17 l3 0 m0 0 l-2 -2 m2 2 l-2 2" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <circle cx="24" cy="34" r="1.5" />
      <circle cx="28" cy="34" r="1.5" opacity={SOFT} />
      <circle cx="32" cy="34" r="1.5" opacity={SOFT} />
    </Frame>
  );
}

// Four tiles at the shape's aspect ratio (Original mixes them).
export function ShapePictogram({ shape }: { shape: GalleryPhotoShape }) {
  const sizes: Record<GalleryPhotoShape, Array<[number, number]>> = {
    SQUARE: [[11, 11], [11, 11], [11, 11]],
    ORIGINAL: [[9, 13], [15, 10], [11, 11]],
    PORTRAIT: [[10, 12.5], [10, 12.5], [10, 12.5]],
    LANDSCAPE: [[14, 9.3], [14, 9.3], [14, 9.3]],
  };
  const tiles = sizes[shape];
  const total = tiles.reduce((sum, [width]) => sum + width, 0) + (tiles.length - 1) * 3;
  let x = (56 - total) / 2;
  return (
    <Frame>
      {tiles.map(([width, height], index) => {
        const tile = (
          <rect key={index} x={x} y={20 - height / 2} width={width} height={height} rx="1" opacity={SOFT} />
        );
        x += width + 3;
        return tile;
      })}
    </Frame>
  );
}

const SPACING_GAPS: Record<GallerySpacing, number> = {
  DESIGN: 3,
  SMALLER: 1.5,
  LARGER: 5.5,
  NONE: 0,
};

// A 3×2 grid whose gaps follow the spacing.
export function SpacingPictogram({ spacing }: { spacing: GallerySpacing }) {
  const gap = SPACING_GAPS[spacing];
  const width = (44 - gap * 2) / 3;
  const height = (30 - gap) / 2;
  return (
    <Frame>
      {[0, 1, 2].map((column) =>
        [0, 1].map((row) => (
          <rect
            key={`${column}-${row}`}
            x={6 + column * (width + gap)}
            y={5 + row * (height + gap)}
            width={width}
            height={height}
            opacity={SOFT}
          />
        ))
      )}
    </Frame>
  );
}
